import React from "react";
import {
  labelForProjectOption,
  toneForProjectStatus,
} from "../lib/projectMetadata";
import { useProjectMetadata } from "../lib/useProjectMetadata";
import { StatusBadge } from "./patterns";
import type { UserDirectoryItem } from "../backend/api/users";

type ProjectStatusBadgeProps = {
  status?: string | null;
};

type ProjectTypeBadgeProps = {
  type?: string | null;
};

type UserRoleBadgeProps = {
  role?: string | null;
};

/**
 * Project status in the shared status treatment. The backend owns the label;
 * the frontend only maps a known code to one of the five tones.
 */
export const ProjectStatusBadge = ({
  status = "",
}: ProjectStatusBadgeProps) => {
  const { metadata } = useProjectMetadata();

  return (
    <StatusBadge tone={toneForProjectStatus(status)}>
      {labelForProjectOption(metadata.statuses, status) || status || "Not set"}
    </StatusBadge>
  );
};

export const ProjectTypeBadge = ({ type = "" }: ProjectTypeBadgeProps) => {
  const { metadata } = useProjectMetadata();

  return (
    <span className="text-sm">
      {labelForProjectOption(metadata.project_types, type) || type || "N/A"}
    </span>
  );
};

export const UserRoleBadge = ({ role = "" }: UserRoleBadgeProps) => (
  <span className="text-sm">{role || "Unknown"}</span>
);

export const UserAccountStatus = ({ user }: {
  user: Pick<UserDirectoryItem, "archived_at" | "account_enabled" | "account_status">;
}) => {
  const disabled = Boolean(user.archived_at) || !user.account_enabled;
  const pending = user.account_status === "invited";
  return (
    <StatusBadge tone={disabled ? "danger" : pending ? "warning" : "success"}>
      {disabled ? "Disabled" : pending ? "Awaiting setup" : "Active"}
    </StatusBadge>
  );
};
